import * as NextNavigation from 'next/navigation';
import { languageTag, setLanguageTag } from '$paraglide/runtime.js';
import { addBasePath, basePath } from '../utils/basePath.js';
import { createLocaliseHref } from '../localiseHref.js';
import { serializeCookie } from '../utils/cookie.js';
import { LANG_COOKIE } from '../constants.js';
import { createRedirects } from './redirect.js';
import { createLink } from './Link.js';

const Navigation = ({ strategy, }) => {
    const Link = createLink(strategy);
    const usePathname = createUsePathname(strategy);
    const { redirect, permanentRedirect } = createRedirects(languageTag, strategy);
    const useRouter = createUseRouter(strategy, usePathname);
    return {
        useRouter,
        usePathname,
        redirect,
        permanentRedirect,
        Link,
    };
};
/**
 * Creates a `usePathname` function that returns the current _canonical_ path.
 * Next's built-in one would return the current _localised_ path.
 */
function createUsePathname(strategy) {
    return () => {
        const encodedLocalisedPathname = NextNavigation.usePathname();
        const localisedPathname = decodeURI(encodedLocalisedPathname);
        return strategy.getCanonicalPath(localisedPathname, languageTag());
    };
}
function createUseRouter(strategy, usePathname) {
    return () => {
        const localiseHref = createLocaliseHref(strategy);
        const nextRouter = NextNavigation.useRouter();
        const searchParams = NextNavigation.useSearchParams();
        const canonicalCurrentPathname = usePathname();
        return {
            ...nextRouter,
            /**
             * Navigate to the provided href. Pushes a new history entry.
             */
            push: (canonicalDestinationPath, options) => {
                const locale = options?.locale ?? languageTag();
                const isLanguageSwitch = locale !== languageTag();
                const localisedPath = localiseHref(canonicalDestinationPath, locale, canonicalCurrentPathname, isLanguageSwitch);
                // If the current and new canonical paths are the same, but the language is different,
                // we need to do a native reload to make sure the new language is used
                if (canonicalCurrentPathname === canonicalDestinationPath && isLanguageSwitch) {
                    let destination = addBasePath(localisedPath, true);
                    const searchParamString = searchParams.toString();
                    if (searchParamString) {
                        destination += `?${searchParamString}`;
                    }
                    history.pushState({}, "", destination);
                    document.cookie = serializeCookie({
                        ...LANG_COOKIE,
                        value: locale,
                        Path: basePath ?? "/",
                    });
                    window.location.reload();
                    return;
                }
                if (options?.locale) {
                    //Make sure to render new client components with the new language
                    setLanguageTag(options.locale);
                }
                return nextRouter.push(localisedPath, options);
            },
            /**
             * Navigate to the provided href. Replaces the current history entry.
             */
            replace: (canonicalDestinationPath, options) => {
                const locale = options?.locale ?? languageTag();
                const isLanguageSwitch = locale !== languageTag();
                const localisedPath = localiseHref(canonicalDestinationPath, locale, canonicalCurrentPathname, isLanguageSwitch);
                // If the current and new canonical paths are the same, but the language is different,
                // we need to do a native reload to make sure the new language is used
                if (canonicalCurrentPathname === canonicalDestinationPath && isLanguageSwitch) {
                    let destination = addBasePath(localisedPath, true);
                    const searchParamString = searchParams.toString();
                    if (searchParamString) {
                        destination += `?${searchParamString}`;
                    }
                    history.replaceState({}, "", destination);
                    document.cookie = serializeCookie({
                        ...LANG_COOKIE,
                        value: locale,
                        Path: basePath ?? "/",
                    });
                    window.location.reload();
                    return;
                }
                if (options?.locale) {
                    //Make sure to render new client components with the new language
                    setLanguageTag(options.locale);
                }
                return nextRouter.replace(localisedPath, options);
            },
            /**
             * Prefetch the provided href.
             */
            prefetch: (canonicalDestinationPath, options) => {
                const locale = options?.locale ?? languageTag();
                const isLanguageSwitch = locale !== languageTag();
                const localisedPath = localiseHref(canonicalDestinationPath, locale, "/", isLanguageSwitch);
                return nextRouter.prefetch(localisedPath, options);
            },
        };
    };
}

export { Navigation };
